/**
 * WHO WOULD BE HIRED TO DO THIS? (spec §68B)
 *
 * Berrier Reno already knows WHAT work a renovation contains
 * (`project_features`), WHERE it happens (room / floor), and WHAT IT SHOULD
 * COST (assemblies → proposed cost lines). It has never known WHO would
 * price or perform it — `expense_categories` answer "which accounting
 * bucket" (Materials, Labor, Contractor, Permits), which is a different
 * question entirely.
 *
 * A trade is that missing answer, and it is what a bid package groups by.
 * ROOM tells a contractor where to go; TRADE decides whose sheet the work
 * belongs on. Grouping by room would put a plumber, an electrician, a
 * cabinet maker and a flooring crew on one page called "Kitchen", which is
 * not how any of them are hired.
 *
 * These are DEFAULTS. Real projects are contracted every which way — one GC
 * for everything, or ten separate subs — so every assignment here can be
 * overridden per project without touching the renovation itself (§68H).
 */

export type TradeId =
  | "plumbing"
  | "electrical"
  | "hvac"
  | "flooring"
  | "tile"
  | "painting-interior"
  | "painting-exterior"
  | "drywall"
  | "carpentry"
  | "cabinetry"
  | "countertops"
  | "doors-windows"
  | "roofing"
  | "siding-exterior"
  | "foundation-structural"
  | "insulation"
  | "demolition"
  | "appliance-install"
  | "landscaping"
  | "concrete"
  | "cleaning"
  | "general-contractor"
  | "owner"
  | "other";

/**
 * HOW A TRADE IS USUALLY BOUGHT.
 *
 * CHANGED IN §76M (carpentry, foundation) AND §79 (doors & windows,
 * cabinetry, countertops, appliance install): these trades now
 * default to trade-wide. Left itemised, baseboard in ten rooms became TEN
 * bid sections with ten price blanks — a worse version of the very problem
 * §76 exists to fix, and every real project had already been folded by
 * hand. §79 work items preserve every per-kind distinction the itemised
 * style existed for — "Exterior doors (4)" and "Interior doors (9)" are
 * separate priced lines inside ONE Doors & Windows section — and any
 * project can still split a trade back into items.
 *
 * `trade-wide`  one bid covering the whole project, with the locations
 *               listed inside it. A plumber prices the house, not the
 *               kitchen and then the bathroom (§68F).
 *
 * `itemised`    the work is discrete enough that each piece is worth
 *               identifying and pointing at on the plan — a window, a
 *               staircase, a run of cabinets (§68E).
 *
 * Neither is a rule. Both are what the package proposes before anybody
 * adjusts it.
 */
export type BidStyle = "trade-wide" | "itemised";

export interface Trade {
  id: TradeId;
  /** The work. */
  displayName: string;
  /** Who does it, in the words somebody would use when hiring them. */
  performer: string;
  defaultStyle: BidStyle;
  sortOrder: number;
}

export const TRADES: readonly Trade[] = [
  { id: "demolition", displayName: "Demolition", performer: "Demolition / General Contractor", defaultStyle: "trade-wide", sortOrder: 10 },
  { id: "foundation-structural", displayName: "Foundation & Structural", performer: "Foundation / Structural Contractor", defaultStyle: "trade-wide", sortOrder: 20 },
  // §80-21: you frame before you roof, so the sheet reads in that order.
  { id: "carpentry", displayName: "Framing & Carpentry", performer: "Framing / Carpentry Contractor", defaultStyle: "trade-wide", sortOrder: 30 },
  { id: "roofing", displayName: "Roofing", performer: "Roofer", defaultStyle: "trade-wide", sortOrder: 40 },
  { id: "siding-exterior", displayName: "Siding & Exterior", performer: "Siding / Exterior Contractor", defaultStyle: "trade-wide", sortOrder: 50 },
  { id: "doors-windows", displayName: "Doors & Windows", performer: "Window / Door Contractor", defaultStyle: "trade-wide", sortOrder: 60 },
  { id: "plumbing", displayName: "Plumbing", performer: "Plumber", defaultStyle: "trade-wide", sortOrder: 70 },
  { id: "electrical", displayName: "Electrical", performer: "Electrician", defaultStyle: "trade-wide", sortOrder: 80 },
  { id: "hvac", displayName: "HVAC", performer: "HVAC Contractor", defaultStyle: "trade-wide", sortOrder: 90 },
  { id: "insulation", displayName: "Insulation", performer: "Insulation Contractor", defaultStyle: "trade-wide", sortOrder: 100 },
  { id: "drywall", displayName: "Drywall", performer: "Drywall Contractor", defaultStyle: "trade-wide", sortOrder: 110 },
  { id: "cabinetry", displayName: "Cabinetry", performer: "Cabinet Contractor", defaultStyle: "trade-wide", sortOrder: 120 },
  { id: "countertops", displayName: "Countertops", performer: "Countertop Fabricator", defaultStyle: "trade-wide", sortOrder: 130 },
  { id: "tile", displayName: "Tile", performer: "Tile Contractor", defaultStyle: "trade-wide", sortOrder: 140 },
  { id: "flooring", displayName: "Flooring", performer: "Flooring Contractor", defaultStyle: "trade-wide", sortOrder: 150 },
  { id: "painting-interior", displayName: "Interior Painting", performer: "Painter", defaultStyle: "trade-wide", sortOrder: 160 },
  { id: "painting-exterior", displayName: "Exterior Painting", performer: "Painter", defaultStyle: "trade-wide", sortOrder: 170 },
  { id: "appliance-install", displayName: "Appliance Installation", performer: "Appliance Installer / Delivery", defaultStyle: "trade-wide", sortOrder: 180 },
  { id: "concrete", displayName: "Concrete & Paving", performer: "Concrete / Site Contractor", defaultStyle: "trade-wide", sortOrder: 190 },
  // Clean-up precedes landscaping on the way out of a job (§80-21).
  { id: "cleaning", displayName: "Cleaning", performer: "Cleanup / General Labor", defaultStyle: "trade-wide", sortOrder: 200 },
  { id: "landscaping", displayName: "Landscaping", performer: "Landscaping Contractor", defaultStyle: "trade-wide", sortOrder: 210 },
  { id: "general-contractor", displayName: "General Contractor", performer: "General Contractor", defaultStyle: "trade-wide", sortOrder: 220 },
  { id: "owner", displayName: "Owner / DIY", performer: "Owner", defaultStyle: "trade-wide", sortOrder: 230 },
  { id: "other", displayName: "Other", performer: "To be determined", defaultStyle: "trade-wide", sortOrder: 240 },
];

const BY_ID = new Map(TRADES.map((t) => [t.id, t]));

export const tradeById = (id: string | null | undefined): Trade | null =>
  id ? (BY_ID.get(id as TradeId) ?? null) : null;

export const isTradeId = (id: string): id is TradeId => BY_ID.has(id as TradeId);

/**
 * THE DEFAULT TRADE FOR EVERY KIND OF WORK BERRIER RENO KNOWS (spec §68C).
 *
 * One entry per seeded feature definition. A definition missing from this
 * table falls back to "other" rather than being guessed at, and shows up in
 * the review surface asking to be assigned — an honest "I don't know" beats
 * quietly filing a beam repair under painting.
 *
 * Some assignments are deliberate judgement calls worth stating:
 *
 *  • CABINETRY and COUNTERTOPS are separate trades. They are frequently
 *    bought from the same company and just as frequently are not, and the
 *    review surface can combine them in one click (§68H). Splitting a
 *    combined bid is harder than combining two.
 *  • BUYING AN APPLIANCE IS NOT A TRADE. An appliance-related feature is
 *    assigned to whoever would actually do the WORK — a dishwasher is a
 *    plumber, a range is an electrician, a range hood is venting — because
 *    that is whose sheet it has to appear on to get priced. "Appliance
 *    Installation" survives as an assignable trade for genuine
 *    delivery-and-hookup scope, but nothing defaults to it: using it as a
 *    catch-all would put plumbing and electrical work on a sheet no
 *    plumber or electrician will ever see.
 *
 *    Whether the contractor SUPPLIES the appliance is a different question
 *    from who installs it. That stays where it already lives: the named
 *    option inside the install assembly (§61F, default off) decides
 *    whether the purchase is in the estimate, and a contractor note says
 *    "Owner supplying appliances — quote labour only" on the sheet.
 *  • Openings (windows and doors) are "Doors & Windows" rather than
 *    Carpentry, because that is who is typically called, and they are
 *    itemised because each one is a specific hole in a specific wall.
 *  • GUTTING and wall removal are DEMOLITION, not the trade that later
 *    works in that space.
 */
export const DEFAULT_TRADE_BY_DEFINITION: Readonly<Record<string, TradeId>> = {
  /* ---- demolition ---- */
  "gut-room": "demolition",
  "remove-interior-wall": "demolition",
  "remove-fixture": "demolition",

  /* ---- structure & foundation ---- */
  "foundation-repair": "foundation-structural",
  "repair-slab-foundation": "foundation-structural",
  "repair-pier-and-beam": "foundation-structural",
  "level-foundation": "foundation-structural",
  "install-piers": "foundation-structural",
  "replace-beams": "foundation-structural",
  "repair-joists": "foundation-structural",
  "repair-subfloor-structure": "foundation-structural",
  "structural-repair": "foundation-structural",

  /* ---- roof & exterior ---- */
  "replace-roof": "roofing",
  "repair-roof": "roofing",
  "replace-roof-decking": "roofing",
  "replace-gutters": "roofing",
  "install-gutters": "roofing",
  "replace-siding": "siding-exterior",
  "exterior-paint": "painting-exterior",
  "replace-fence": "landscaping",
  "landscaping": "landscaping",
  "repair-driveway": "concrete",
  "build-deck": "carpentry",

  /* ---- framing & carpentry ---- */
  "construct-interior-wall": "carpentry",
  "build-interior-wall": "carpentry",
  "replace-baseboard": "carpentry",
  "install-crown-molding": "carpentry",
  "replace-closet-system": "carpentry",
  "install-staircase": "carpentry",
  "replace-staircase": "carpentry",
  "refinish-stairs": "carpentry",
  "replace-treads-risers": "carpentry",
  "install-handrail": "carpentry",
  "install-guardrail": "carpentry",
  "repair-stair-structure": "foundation-structural",

  /* ---- openings ---- */
  // Removing an opening is NOT general demolition: it is a specific hole in
  // a specific wall, and the brief calls it out on the plan by name
  // ("Q-01 — KITCHEN WINDOW"). Gutting a room stays demolition.
  "remove-window-opening": "doors-windows",
  "remove-door-opening": "doors-windows",
  "replace-window": "doors-windows",
  "install-window": "doors-windows",
  "replace-interior-door": "doors-windows",
  "install-interior-door": "doors-windows",
  "replace-exterior-door": "doors-windows",
  "replace-garage-door": "doors-windows",
  "replace-door-hardware": "doors-windows",
  "install-blinds": "doors-windows",

  /* ---- plumbing ---- */
  "replace-kitchen-sink": "plumbing",
  "replace-kitchen-faucet": "plumbing",
  "replace-bathroom-faucet": "plumbing",
  "replace-garbage-disposal": "plumbing",
  "replace-toilet": "plumbing",
  "replace-tub": "plumbing",
  "replace-shower": "plumbing",
  "replace-water-heater": "plumbing",
  "whole-house-repipe": "plumbing",
  "install-washer": "plumbing",
  "install-laundry-stacked": "plumbing",
  "install-laundry-combo": "plumbing",

  /* ---- electrical ---- */
  "replace-outlets-switches": "electrical",
  "replace-light-fixture": "electrical",
  "install-ceiling-fan": "electrical",
  "install-smoke-detectors": "electrical",
  "replace-electrical-panel": "electrical",
  "replace-vanity-light": "electrical",
  "replace-exhaust-fan": "electrical",
  "install-dryer": "electrical",

  /* ---- mechanical ---- */
  "replace-hvac": "hvac",

  /* ---- insulation & drywall ---- */
  "install-insulation": "insulation",
  "repair-drywall": "drywall",

  /* ---- cabinets and tops ---- */
  "replace-cabinets": "cabinetry",
  "reface-cabinets": "cabinetry",
  "replace-vanity": "cabinetry",
  "replace-countertops": "countertops",
  "replace-vanity-top": "countertops",

  /* ---- appliance WORK, filed under whoever performs it (spec §68C) ----
   * The appliance itself is a product the owner usually buys; what a
   * contractor prices is the connection it needs.
   */
  // Range: the circuit (or the gas line, which the plumber runs here).
  "replace-range": "electrical",
  // A hood is a duct through a wall or roof before it is anything else.
  "replace-range-hood": "hvac",
  // Supply, drain and a dedicated circuit — the plumber leads.
  "replace-dishwasher": "plumbing",
  // A microwave is a circuit and a cabinet opening.
  "replace-microwave": "electrical",
  // What a refrigerator actually needs installed is a water line.
  "replace-refrigerator": "plumbing",

  /* ---- tile & wet areas ---- */
  "install-backsplash": "tile",
  "install-tile-floor": "tile",
  "install-shower-tile": "tile",
  "install-tub-shower-surround": "tile",
  "install-waterproofing": "tile",
  "install-bath-accessories": "carpentry",
  "replace-bathroom-mirror": "carpentry",

  /* ---- flooring ---- */
  "replace-flooring": "flooring",
  "refinish-hardwood": "flooring",

  /* ---- painting ---- */
  "paint-walls": "painting-interior",
  "paint-ceiling": "painting-interior",

  /* ---- whole-project work every job carries (spec §89) ---- */
  "dumpster-debris-removal": "demolition",
  "final-cleanup": "cleaning",
  "permits-fees": "general-contractor",
  "plans-drawings": "general-contractor",
  "general-contractor-fee": "general-contractor",

  /* ---- not construction work at all ---- */
  "professional-services": "other",
  "mileage": "owner",
  "fuel": "owner",
  "miscellaneous": "other",
};

/** The trade a kind of work belongs to when nobody has said otherwise. */
export function defaultTradeFor(featureDefinitionId: string | null | undefined): TradeId {
  if (!featureDefinitionId) return "other";
  return DEFAULT_TRADE_BY_DEFINITION[featureDefinitionId] ?? "other";
}

/**
 * DEMOLITION MEANS ALL DEMOLITION (spec §74D — permanent).
 *
 * Tear-out is classified by the ACTION, not by what is being torn out:
 * removing a wall, a floor, a cabinet run, a toilet, a door or a window is
 * all the demo crew's work and rolls into DEMOLITION downstream — budgets,
 * bid sheets, actual-cost rollups alike. The replacement installation
 * belongs to its own trade. (This deliberately supersedes §68's
 * opening-removal-as-doors-windows default.) An explicit per-project
 * decision still beats this rule, exactly as it beats the definition table.
 */
export function defaultTradeForWork(
  featureDefinitionId: string | null | undefined,
  action: string | null | undefined,
): TradeId {
  if (action === "remove" || action === "demolish") return "demolition";
  return defaultTradeFor(featureDefinitionId);
}
